import { classList } from '@findigs/helpers/src/helpers/class-list';
import { useDisableAutoFillOnLoad } from '@findigs/helpers/src/hooks/use-disable-autofill-on-load';
import { useInputTrimOnBlur } from '@findigs/helpers/src/hooks/use-input-trim-on-blur';
import type { JSX } from 'react';
import React, { useCallback, useId } from 'react';
import { styles } from './form-input.module.scss';

export type FormInputProps = Omit<
  React.HTMLProps<HTMLInputElement>,
  'as' | 'ref'
> & {
  label: string;
  hint?: string | JSX.Element;
  prefix?: string;
  suffix?: string | JSX.Element;
  error?: FormFieldError;

  /** @default false */
  required?: boolean;

  /**
   * Controls whether the input should be automatically trimmed when
   * it loses focus (blur event)
   * @default true
   * */
  trim?: boolean;

  /**
   * Controls whether "(optional)" should be displayed for non-required fields
   * @default true
   * */
  autoOptional?: boolean;
};

/**
 * Extends the vanilla `input` element with our styling.
 * All vanilla `input` props are forwarded.
 */
export const FormInput = React.forwardRef(
  (
    {
      label,
      hint,
      prefix,
      suffix,
      error,
      style,
      className,
      placeholder,
      id,
      type,
      required,
      disabled,
      trim = true,
      onBlur: forwardedOnBlur,
      readOnly: forwardedReadOnly,
      onChange,
      children,
      autoOptional = true,
      ...props
    }: FormInputProps,
    ref: React.ForwardedRef<HTMLInputElement>
  ) => {
    const _id = useId();

    const { readOnly } = useDisableAutoFillOnLoad(id ?? _id, forwardedReadOnly);

    const onParentClick = useCallback((e: React.MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.classList.contains('input')) {
        const input = target?.querySelector('input');
        if (document.activeElement !== input) {
          input?.focus();
        }
        e.preventDefault();
      }
    }, []);

    const { onBlur } = useInputTrimOnBlur({
      enabled: trim,
      onChange,
      forwardedOnBlur,
    });

    const classesOuter = classList(styles, className);
    const classesInner = classList('input', { disabled, invalid: error });
    return (
      <div className={classesOuter} style={style}>
        <div className={classesInner} onMouseDown={onParentClick}>
          {prefix ? <p className="prefix">{prefix}</p> : null}
          <input
            id={id ?? _id}
            placeholder={placeholder ?? ' '}
            ref={ref}
            type={type ?? 'text'}
            onBlur={onBlur}
            onChange={onChange}
            {...props}
            aria-invalid={props['aria-invalid'] ?? error != null}
            required={required}
            readOnly={readOnly}
            disabled={disabled}
          />
          <label htmlFor={id ?? _id}>
            {label}
            {required || !autoOptional ? null : (
              <span className="text-muted"> (optional)</span>
            )}
          </label>
          {suffix ? (
            typeof suffix === 'string' ? (
              <p className="suffix">{suffix}</p>
            ) : (
              suffix
            )
          ) : null}
        </div>
        {!disabled && error?.message ? (
          <p className="error" role="alert">
            {error?.message}
          </p>
        ) : hint ? (
          <>
            <p className="hint" role="alert">
              {hint}
            </p>
          </>
        ) : null}
        {children}
      </div>
    );
  }
);
FormInput.displayName = 'FormInput';
